import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getTags, createTag, updateTag, deleteTag, reorderTags } from '../../services/campaignTags';
import type { CampaignTag } from '../../types/campaign';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';

const PRESET_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
  '#64748b', // Slate
];

export const TagManagerPage: React.FC = () => {
  const { t } = useTranslation('tags');
  const [tags, setTags] = useState<CampaignTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTag, setEditingTag] = useState<CampaignTag | null>(null);
  const [deleteConfirmTag, setDeleteConfirmTag] = useState<CampaignTag | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState(PRESET_COLORS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setIsLoading(true);
    try {
      const data = await getTags();
      setTags(data);
    } catch (error) {
      console.error('Failed to load tags:', error);
      toast.error(t('messages.failed_load'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formName.trim()) {
      toast.error(t('messages.name_required'));
      return;
    }

    setIsSubmitting(true);
    try {
      const newTag = await createTag({ name: formName.trim(), color: formColor });
      setTags([...tags, newTag]);
      setFormName('');
      setFormColor(PRESET_COLORS[0]);
      setIsCreateModalOpen(false);
      toast.success(t('messages.created'));
    } catch (error: any) {
      console.error('Failed to create tag:', error);
      const errorMessage = error.response?.data?.message || t('messages.failed_create');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTag = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingTag || !formName.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedTag = await updateTag(editingTag.id, {
        name: formName.trim(),
        color: formColor,
      });
      setTags(tags.map((t) => (t.id === updatedTag.id ? updatedTag : t)));
      setEditingTag(null);
      setFormName('');
      setFormColor(PRESET_COLORS[0]);
      toast.success(t('messages.updated'));
    } catch (error: any) {
      console.error('Failed to update tag:', error);
      const errorMessage = error.response?.data?.message || t('messages.failed_update');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTag = async () => {
    if (!deleteConfirmTag) return;

    setIsSubmitting(true);
    try {
      await deleteTag(deleteConfirmTag.id);
      setTags(tags.filter((t) => t.id !== deleteConfirmTag.id));
      setDeleteConfirmTag(null);
      toast.success(t('messages.deleted'));
    } catch (error: any) {
      console.error('Failed to delete tag:', error);
      const errorMessage = error.response?.data?.message || t('messages.failed_delete');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveTag = async (tagId: number, direction: 'up' | 'down') => {
    const index = tags.findIndex((t) => t.id === tagId);
    if (index === -1) return;

    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === tags.length - 1) return;

    const newTags = [...tags];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newTags[index], newTags[targetIndex]] = [newTags[targetIndex], newTags[index]];

    // Optimistically update UI
    setTags(newTags);

    try {
      await reorderTags(newTags.map((t) => t.id));
    } catch (error: any) {
      console.error('Failed to reorder tags:', error);
      toast.error(t('messages.failed_reorder'));
      // Revert on error
      loadTags();
    }
  };

  const openEditModal = (tag: CampaignTag) => {
    setEditingTag(tag);
    setFormName(tag.name);
    setFormColor(tag.color);
  };

  const closeEditModal = () => {
    setEditingTag(null);
    setFormName('');
    setFormColor(PRESET_COLORS[0]);
  };

  const openCreateModal = () => {
    setFormName('');
    setFormColor(PRESET_COLORS[0]);
    setIsCreateModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('page.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('page.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('page.subtitle')}
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t('actions.create')}
        </button>
      </div>

      {/* Tags list */}
      {tags.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('empty.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('empty.description')}</p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t('actions.create_first')}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {tags.map((tag, index) => (
              <li key={tag.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Tag preview */}
                    <div
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color,
                        border: `1px solid ${tag.color}40`,
                      }}
                    >
                      {tag.name}
                    </div>

                    {/* Color indicator */}
                    <div className="flex items-center text-xs text-gray-500">
                      <div
                        className="w-4 h-4 rounded-full mr-1.5 border border-gray-300"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.color}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {/* Move up/down */}
                    <div className="flex border border-gray-300 rounded overflow-hidden">
                      <button
                        onClick={() => handleMoveTag(tag.id, 'up')}
                        disabled={index === 0}
                        className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={t('actions.move_up')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveTag(tag.id, 'down')}
                        disabled={index === tags.length - 1}
                        className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed border-l border-gray-300"
                        title={t('actions.move_down')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Edit */}
                    <button
                      onClick={() => openEditModal(tag)}
                      className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 focus:outline-none"
                    >
                      {t('actions.edit')}
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => setDeleteConfirmTag(tag)}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 focus:outline-none"
                    >
                      {t('actions.delete')}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={isCreateModalOpen}
        onClose={() => !isSubmitting && setIsCreateModalOpen(false)}
        title={t('modal.create_title')}
      >
        <form onSubmit={handleCreateTag} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('form.name_label')}</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder={t('form.name_placeholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={isSubmitting}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('form.color_label')}</label>
            <div className="flex gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormColor(color)}
                  className={`w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 ${
                    formColor === color ? 'ring-2 ring-offset-2 ring-gray-600' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  disabled={isSubmitting}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>
            <div className="mt-2">
              <input
                type="text"
                value={formColor}
                onChange={(e) => setFormColor(e.target.value)}
                placeholder={t('form.color_placeholder')}
                className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {t('actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formName.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isSubmitting ? t('status.creating') : t('actions.create')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editingTag}
        onClose={() => !isSubmitting && closeEditModal()}
        title={t('modal.edit_title')}
      >
        <form onSubmit={handleUpdateTag} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('form.name_label')}</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('form.color_label')}</label>
            <div className="flex gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormColor(color)}
                  className={`w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 ${
                    formColor === color ? 'ring-2 ring-offset-2 ring-gray-600' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  disabled={isSubmitting}
                />
              ))}
            </div>
            <div className="mt-2">
              <input
                type="text"
                value={formColor}
                onChange={(e) => setFormColor(e.target.value)}
                className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={closeEditModal}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {t('actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formName.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isSubmitting ? t('status.saving') : t('actions.save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteConfirmTag}
        onClose={() => !isSubmitting && setDeleteConfirmTag(null)}
        title={t('modal.delete_title')}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('modal.delete_message', { name: deleteConfirmTag?.name })}
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setDeleteConfirmTag(null)}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {t('actions.cancel')}
            </button>
            <button
              onClick={handleDeleteTag}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              {isSubmitting ? t('status.deleting') : t('actions.delete')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

TagManagerPage.displayName = 'TagManagerPage';
